import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';

export type DpiaAdminRowActionContext = Pick<AssetProcessingActivityReviewRow, 'dpiaDisplayStatus' | 'dpiaRequestStatus' | 'dpiaId'>;

export const canShowDpiaHistoryAction = (
  row: Pick<AssetProcessingActivityReviewRow, 'hasPreviousDpias' | 'dpiaCount' | 'dpiaRequestStatus'>,
): boolean =>
  Boolean(
    row.hasPreviousDpias ||
      (row.dpiaCount ?? 0) > 1 ||
      ((row.dpiaCount ?? 0) > 0 && ['indicated', 'draft'].includes(row.dpiaRequestStatus ?? '')),
  );

export const canStartDpiaReassessment = (status: string | undefined, canEdit = true): boolean => canEdit && status === 'decision_made';

const DPIA_INDICATION_AWAITING_RA_ASSESSMENT_DISPLAY_STATUSES = ['indicated'] as const;

export const isDpiaRequestAwaitingRaAssessment = (ctx: DpiaAdminRowActionContext): boolean =>
  ctx.dpiaRequestStatus === 'draft' || ctx.dpiaRequestStatus === 'indicated';

export const isDpiaIndicationAwaitingRaAssessment = (ctx: DpiaAdminRowActionContext): boolean =>
  DPIA_INDICATION_AWAITING_RA_ASSESSMENT_DISPLAY_STATUSES.includes(
    ctx.dpiaDisplayStatus as typeof DPIA_INDICATION_AWAITING_RA_ASSESSMENT_DISPLAY_STATUSES[number],
  );

export const isDpiaFormAwaitingAdminReview = (ctx: DpiaAdminRowActionContext): boolean =>
  ['ready_for_review', 'clarification_requested'].includes(ctx.dpiaDisplayStatus);

export const shouldShowOpenAdminDpiaReview = (ctx: DpiaAdminRowActionContext): boolean =>
  Boolean(ctx.dpiaId) && isDpiaFormAwaitingAdminReview(ctx);

export const getAdminDpiaNeedAssessmentAction = (ctx: DpiaAdminRowActionContext, canAssessDpia: boolean): 'reassess' | 'assess' | null => {
  if (!canAssessDpia) {
    return null;
  }

  if (isDpiaFormAwaitingAdminReview(ctx)) {
    return null;
  }

  if (isDpiaRequestAwaitingRaAssessment(ctx)) {
    return 'assess';
  }

  if (!ctx.dpiaRequestStatus && isDpiaIndicationAwaitingRaAssessment(ctx)) {
    return 'assess';
  }

  return null;
};
